/**
 * Module for displaying AI guidance messages to the user at the start of a task.
 */

import { util } from '../../common/utilities.js';
/** @typedef {import('../Main.js').ExploreConfig} ExploreConfig */
/** @typedef {import('./PopUpMessage.js').PopUpMessage} PopUpMessage */
/** @typedef {import('../task/TaskContainer.js').TaskContainer} TaskContainer */
/** @typedef {import('../data/Tracker.js').Tracker} Tracker */

export class AiGuidance {
  #taskContainer;
  #popUpMessage;
  #tracker;

  /**
   * @param {ExploreConfig} config
   * @param {TaskContainer} taskContainer - Whose current street is described, each time it changes. Not on free
   *   exploration's street switches, which can come every move.
   * @param {PopUpMessage} popUpMessage
   * @param {Tracker} tracker
   */
  constructor(config, taskContainer, popUpMessage, tracker) {
    this.#taskContainer = taskContainer;
    this.#popUpMessage = popUpMessage;
    this.#tracker = tracker;
    if (!config.isExploreAddressMode) taskContainer.onCurrentTaskChanged(() => this.showAiGuidanceMessage());
  }

  /**
   * Analyzes the current street using Gemini AI and returns guidance for the user.
   * @returns {Promise<null|AuthenticatorResponse|any>}
   */
  async #analyzeCurrentStreetWithGemini(currTask) {
    try {
      // Prepare the request payload.
      const payload = {
        way_type: currTask.getProperty('wayType'),
        street_edge_id: currTask.getProperty('streetEdgeId'),
      };

      // Send request to the backend.
      const response = await fetch('/ai/analyzeScene', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        return result.response;
      } else {
        console.error('Analysis failed:', result.error);
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error analyzing canvas:', error);
      throw error;
    }
  }

  /**
   * Displays a pop-up message with AI-generated guidance based on the current street. Pilot in Chandigarh, India.
   */
  showAiGuidanceMessage() {
    if (window.cityId === 'chandigarh-india') {
      this.#analyzeCurrentStreetWithGemini(this.#taskContainer.getCurrentTask())
        .then((response) => {
          const title = 'Mission Guidance';
          const aiNote = 'This was generated by AI using imagery from this street.';
          const iconAttrs = `role="img" aria-label="${aiNote}" data-ps-tooltip="${aiNote}" tabindex="0"`;
          const infoIcon = `<span ${iconAttrs}>&#9432;</span>`;
          const body = `<span>${util.escapeHTML(response)}  ${infoIcon}</span>`;

          // Show the pop-up message with the AI guidance.
          this.#popUpMessage.notify(title, body, () => {
            // Log the message shown to the user.
            this.#tracker.push('PopUpShow_AiGuidance', { message: response });
          });

          // Add the AI guidance just above the minimap, with full text on hover in case it's too long to fit.
          const status = document.getElementById('mission-guidance-status');
          if (status) {
            status.textContent = response;
            status.title = response;
          }
        })
        .catch((error) => {
          this.#tracker.push('PopUpShow_AiGuidance_Error', { error: error.message });
          console.error('Error showing AI guidance:', error);
        });
    }
  }
}
